import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {Avatar} from '@components/common/base/avatar'
import {TableColumn} from '@components/common/base/table'
import {DraggableNative} from '@components/draggable-native'
import {cn} from '@containers/html-utils'
import {HighlightQuery} from './components/highlight-query'
import {ToggleSelectAll} from './components/toggle-select-all'
import {UserCheckbox} from './components/user-checkbox'
import {UserTooltip} from './components/user-tooltip'
import {TableUserInfo, UsersTableState, UserTablesProps} from './types'
import {UsersTableStore} from './users-table-store'

const UserAvatar = ({user, large}: {user: TableUserInfo; large?: boolean}) => (
  <Avatar
    {...user}
    size={large ? 24 : 16}
    className="shrink-0 cursor-pointer"
    hideOutline
    hideHint
  />
)

const UserPill = tw.div`
  border
  pr-6
  max-w-full
  inline-block
  border-solid
  border-transparent
  hover:border-main-old
  hover:bg-header-old
  rounded-full
`

const getTooltipColumn = (): TableColumn<TableUserInfo> => {
  return {
    title: '',
    width: 0,
    className: '!px-0',
    render: u => <UserTooltip user={u} />
  }
}

const getSelectColumn = ({
  useCheckbox
}: {
  useCheckbox: boolean
}): TableColumn<TableUserInfo> => {
  return {
    oldSortStyle: true,
    title: useCheckbox ? <ToggleSelectAll /> : '',
    width: useCheckbox ? 30 : 32,
    className: 'px-8',
    render: x => (
      <div className="flex items-center">
        <UserCheckbox userId={x.userId} />
      </div>
    )
  }
}

const getPositionColumn = ({
  large,
  fixedColumnWidth
}: {
  large: boolean
  fixedColumnWidth: boolean
}): TableColumn<TableUserInfo> => {
  const width = large ? 200 : 104
  return {
    oldSortStyle: true,
    title: Lang.msg.companies.position,
    width: fixedColumnWidth ? width : undefined,
    className: 'flex-1',
    sortBy: x => x.position,
    render: x => <HighlightQuery text={x.position} />
  }
}

const getDepartmentColumn = ({
  large,
  fixedColumnWidth
}: {
  large: boolean
  fixedColumnWidth: boolean
}): TableColumn<TableUserInfo> => {
  const width = large ? 200 : 104
  return {
    oldSortStyle: true,
    title: Lang.msg.companies.department,
    width: fixedColumnWidth ? width : undefined,
    className: 'flex-1',
    sortBy: x => x.departments,
    render: x => <HighlightQuery text={x.departments} />
  }
}

const getRestColumns = (
  store: UsersTableStore,
  state: UsersTableState,
  props: UserTablesProps
): (TableColumn<TableUserInfo> & {
  defaultSort?: boolean
})[] => {
  const {draggableUsers, disableClickableRows} = props
  return [
    {
      oldSortStyle: true,
      title: Lang.msg.name,
      sortBy: x => x.userName,
      defaultSort: false,
      className: 'flex-1',
      render(user) {
        const userView = (
          <div
            ref={ref => ref && store.setUserElement(user.userId, ref)}
            className={cn(
              'flex items-center truncate',
              draggableUsers ? 'cursor-move' : 'cursor-pointer',
              'px-8'
            )}
            onClick={
              disableClickableRows
                ? e => {
                    e.stopPropagation()
                    const {userId} = user
                    store.toggleSelected(userId)
                    props.onToggled?.([userId])
                    props.onSelectedChanged?.([...state.selected])
                  }
                : undefined
            }
          >
            <UserAvatar user={user} large={props.large} />
            <HighlightQuery className="ml-4" text={user.userName} />
          </div>
        )

        return draggableUsers ? (
          <DraggableNative transfer={{type: 'user', userId: user.userId}}>
            <UserPill>{userView}</UserPill>
          </DraggableNative>
        ) : (
          userView
        )
      }
    }
  ]
}

export const createColumns = (
  store: UsersTableStore,
  state: UsersTableState,
  props: UserTablesProps
): (TableColumn<TableUserInfo> & {
  defaultSort?: boolean
})[] => {
  const {
    useCheckbox = false,
    showPositionColumn = false,
    lastColumn,
    large = false,
    hideDepartmentColumn = false,
    fixedColumnWidth = false
  } = props

  const tooltipColumn: TableColumn<TableUserInfo> = getTooltipColumn()
  const selectColumn: TableColumn<TableUserInfo> = getSelectColumn({
    useCheckbox
  })
  const positionColumn: TableColumn<TableUserInfo> = getPositionColumn({
    large,
    fixedColumnWidth
  })
  const departmentColumn: TableColumn<TableUserInfo> = getDepartmentColumn({
    large,
    fixedColumnWidth
  })
  const restColumns: (TableColumn<TableUserInfo> & {
    defaultSort?: boolean
  })[] = getRestColumns(store, state, props)

  return [
    tooltipColumn,
    useCheckbox && selectColumn,
    ...restColumns,
    !hideDepartmentColumn && departmentColumn,
    showPositionColumn && positionColumn,
    !useCheckbox && selectColumn,
    lastColumn
  ].filter(Util.isDefined)
}
